/**
 * B-GTCC-UI1 — static component gallery for every Cockpit content
 * primitive under `apps/admin/src/gtcc/ui/`. Reachable only via the
 * `ui-gallery` deep-link wired in `GtccShell.tsx` (never listed in the
 * menu/registry — see that file's `isGtccUiGalleryLocation()`).
 *
 * Every example value below is copied verbatim from
 * `GTCC_REFERENCE_2026-09-01.html` (Executive Summary tab, Infrastructure &
 * VPS Health tab, Historical Trends tab, and the closing
 * "Entscheidungs-Punkte" section) — nothing here is invented. The view is
 * fully static: fixed strings/numbers only, no timers, no randomness, no
 * network calls.
 */
import {
  Bar,
  Chip,
  ChartPanel,
  DecisionCard,
  DecisionQuestion,
  DecisionsSection,
  DiskCell,
  Dot,
  DonutLegend,
  DonutWrap,
  Duo,
  FreshBadge,
  GridBottom,
  GridMain,
  HostCell,
  Legend,
  Meter,
  MockBanner,
  NetCell,
  Panel,
  Pill,
  Row,
  Tabs,
  TableWrap,
  Tile,
  TilesGrid,
  TriGrid,
} from "./primitives.js";
import { BarChart, Donut, LineChart, Sparkline } from "./charts.js";
import "./cockpit-fonts.css";
import "./cockpit-ui.css";

export function GalleryView() {
  return (
    <div className="gtcc-ui-gallery" data-testid="gtcc-ui-gallery">
      <MockBanner>
        UI-Gallery (B-GTCC-UI1) — statische Vorschau aller Cockpit-Bausteine. Beispieldaten aus
        GTCC_REFERENCE_2026-09-01.html, nur per Deep-Link „ui-gallery" erreichbar, nicht im Menü.
      </MockBanner>

      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1>Monitoring Cockpit</h1>
        <FreshBadge label="aktualisiert vor 32 s · vollständig" />
      </div>

      <Tabs
        items={[
          { key: "exec", label: "Executive Summary" },
          { key: "infra", label: "Infrastructure & VPS Health" },
          { key: "svc", label: "Services & Dependencies" },
          { key: "rules", label: "Rules & Telemetry" },
          { key: "delivery", label: "Delivery & Watchdog" },
          { key: "trends", label: "Historical Trends" },
        ]}
        activeKey="exec"
      />

      <Tabs
        items={[
          { key: "overview", label: "Overview" },
          { key: "hosts", label: "Hosts" },
          { key: "storage", label: "Storage" },
          { key: "network", label: "Network" },
          { key: "backups", label: "Backups" },
          { key: "haertung", label: "Härtung" },
        ]}
        activeKey="overview"
        sm
      />

      <TilesGrid>
        <Tile
          tone="ok"
          title="PLATFORM HEALTH"
          badge={<FreshBadge label="32 s" />}
          value="Healthy"
          statusTone="ok"
          status="Alle Pflicht-Signale frisch & vollständig"
        />
        <Tile
          tone="crit"
          title="ACTIVE ALERTS"
          value="2"
          valueSuffix="krit · 1 warn"
          statusTone="crit"
          status="▲ GTOffsiteCheckFailed · seit 41 min"
        />
        <Tile
          tone="ok"
          title="ACTIVE INCIDENTS"
          value="0"
          statusTone="ok"
          status="Kein Incident offen"
        />
        <Tile
          tone="warn"
          title="DATA CONFIDENCE"
          badge={<FreshBadge label="1 Quelle stale" stale />}
          value="96,8"
          valueSuffix="%"
          statusTone="warn"
          status="gt-backup-1 Metriken 12 min alt"
        />
        <Tile
          tone="info"
          title="UPTIME (30 T)"
          value="99,97"
          valueSuffix="%"
          statusTone="info"
          status="SLO 99,9 % · Budget 71 % übrig"
        />
        {/* B-GTCC-UI-ADOPT1: muted-tone example — "nicht messbar", NIE als
            ok/info gezeigt (Ehrlichkeitsregel). */}
        <Tile
          tone="muted"
          title="OFFSITE-METRIK"
          value="—"
          statusTone="muted"
          status="nicht messbar — Quelle stale"
        />
      </TilesGrid>

      <GridMain>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          <Panel title="Infrastructure & VPS Health" badge={<FreshBadge label="32 s" />}>
            <TableWrap>
              <tbody>
                <tr>
                  <th>HOST</th>
                  <th>STATUS</th>
                  <th>LATENZ</th>
                  <th>CPU (§1.9)</th>
                  <th>MEMORY</th>
                  <th>DISK · FORECAST</th>
                  <th>NETWORK (IN/OUT)</th>
                </tr>
                <tr>
                  <HostCell name="gt-app-main-1" detail="API · Edge · Auth" />
                  <td>
                    <Dot tone="ok" />
                    Healthy
                  </td>
                  <td>
                    <Sparkline points="0,14 9,12 18,15 27,9 36,11 45,7 54,10 63,8 72,9" tone="ok" />
                    <span className="mono">28 ms</span>
                  </td>
                  <td>
                    <Meter tone="cool" pct={24} caption="8 vCPU" />
                  </td>
                  <td>
                    <Meter tone="violet" pct={42} caption="9,3 GiB frei" />
                  </td>
                  <td>
                    <DiskCell
                      pct={62}
                      usedLabel="Used 62 % (142 GB)"
                      freeLabel="Free 87 GiB"
                      forecast="Prognose: > 90 Tage bis voll"
                    />
                  </td>
                  <NetCell up="↑ 120 Mbps" down="↓ 80 Mbps" />
                </tr>
                <tr>
                  <HostCell name="gt-admin-bastion-1" detail="Bastion · WireGuard" />
                  <td>
                    <Dot tone="warn" />
                    Warning
                  </td>
                  <td>
                    <Sparkline
                      points="0,13 9,10 18,14 27,7 36,12 45,5 54,11 63,6 72,9"
                      tone="warn"
                    />
                    <span className="mono">62 ms</span>
                  </td>
                  <td>
                    <Meter tone="hot" pct={68} caption="2 vCPU" />
                  </td>
                  <td>
                    <Meter tone="hot" pct={71} caption="1,1 GiB frei" />
                  </td>
                  <td>
                    <DiskCell
                      pct={82}
                      usedLabel="Used 82 % (33 GB)"
                      freeLabel="Free 7,2 GiB"
                      forecast="Prognose: 19 Tage bis voll"
                      forecastTone="warn"
                      hot
                    />
                  </td>
                  <NetCell up="↑ 12 Mbps" down="↓ 9 Mbps" />
                </tr>
                <tr>
                  <HostCell name="gt-backup-1" detail="Borg · Storage-Box-Anbindung" />
                  <td>
                    <Dot tone="warn" />
                    Stale
                  </td>
                  <td>
                    <Sparkline points="0,11 9,12 18,10 27,12 36,11 45,12" tone="warn" dashed />
                    <span className="mono muted">— 12 min alt</span>
                  </td>
                  <td>
                    <span className="mono muted">
                      unbekannt — Stale wird NIE als Healthy gezeigt
                    </span>
                  </td>
                  <td>
                    <span className="mono muted">—</span>
                  </td>
                  <td>
                    <span className="mono">
                      lokal 34 % ·{" "}
                      <b className="muted">Storage Box = REMOTE, eigene Zeile (GT-OPEN-123)</b>
                    </span>
                  </td>
                  <NetCell muted />
                </tr>
              </tbody>
            </TableWrap>
          </Panel>

          <Duo trio>
            <Panel title="Fahrzeug-Datenfluss" badge={<FreshBadge label="1 min" />}>
              <Row tone="ok" value="241 / 266">
                Senden aktuell (15 min)
              </Row>
              <Row tone="warn" value="18 Fahrzeuge">
                Still &gt; 24 h
              </Row>
              <Row tone="ok" value="1 240">
                Positionen / min
              </Row>
              <Row tone="ok" value="139 / 155">
                Größter Tenant (URBAGEC)
              </Row>
            </Panel>
            <Panel title="Backup & Recovery Readiness" badge={<FreshBadge label="5 min" />}>
              <Row tone="ok" value="02:31 ✓">
                Class 2 (App) letzter Lauf
              </Row>
              <Row tone="ok" value="03:30 ✓">
                Class 3 (Keycloak) letzter Lauf
              </Row>
              <Row tone="crit" value="FEHLGESCHLAGEN" valueTone="crit">
                Offsite-Prüfung
              </Row>
              <Row tone="ok" value="Keycloak ✓">
                Restore-bewiesen (KRP)
              </Row>
            </Panel>
            <Panel title="Jobs & Timer" badge={<FreshBadge label="5 min" />}>
              <Row tone="ok" value="vor 22 min ✓">
                Geocode-Backfill (stündl.)
              </Row>
              <Row tone="ok" value="03:15 ✓ · 0 fällig">
                DSGVO-Löschlauf (tägl.)
              </Row>
              <Row tone="ok" value="03:15 ✓">
                Telemetrie-Retention (tägl.)
              </Row>
              <Row tone="ok" value="Flag aus (E2)" muted>
                Wartungs-Erinnerungen
              </Row>
              <Row tone="muted" value="—">
                Offsite-Prüfung (stale, nicht messbar)
              </Row>
            </Panel>
          </Duo>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <ChartPanel title="Latenz p95" badge={<span className="mono muted">24 h</span>}>
            <Legend
              items={[
                { tone: "accent", label: "API" },
                { tone: "info", label: "DB" },
              ]}
            />
            <LineChart
              height={60}
              baselineY={30}
              series={[
                {
                  tone: "accent",
                  points: "0,40 30,38 60,43 90,33 120,39 150,29 180,38 210,26 240,36 270,31 300,34",
                },
                {
                  tone: "info",
                  points: "0,50 30,49 60,51 90,48 120,50 150,49 180,50 210,48 240,50 270,49 300,50",
                },
              ]}
              markers={[{ cx: 300, cy: 34, tone: "accent" }]}
            />
          </ChartPanel>
          <ChartPanel
            title="Telemetrie-Eingang"
            badge={<span className="mono muted">Positionen/min · 24 h</span>}
          >
            <Legend
              items={[
                { tone: "accent", label: "gesamt" },
                { note: "Einbruch = Flotte stumm — die 266er-Lehre als Kurve" },
              ]}
            />
            <LineChart
              height={70}
              baselineY={35}
              series={[
                {
                  tone: "accent",
                  points:
                    "0,22 25,20 50,24 75,21 100,23 125,48 150,50 175,24 200,22 225,25 250,21 275,23 300,22",
                },
              ]}
              markers={[{ cx: 137, cy: 49, tone: "warn" }]}
              annotations={[{ x: 137, y: 62, text: "04:10–04:40 Gateway-Neustart", tone: "warn" }]}
            />
          </ChartPanel>
        </div>
      </GridMain>

      <GridBottom>
        <Panel title="Alert Severity" badge={<span className="mono muted">7 Tage</span>}>
          <DonutWrap>
            <Donut
              segments={[
                { tone: "crit", value: 14 },
                { tone: "warn", value: 29 },
                { tone: "info", value: 36 },
                { tone: "accent", value: 21 },
              ]}
              centerValue="14"
              centerLabel="ALERTS"
            />
            <DonutLegend
              items={[
                { tone: "crit", label: "Critical 2 (14 %)" },
                { tone: "warn", label: "High 4 (29 %)" },
                { tone: "info", label: "Medium 5 (36 %)" },
                { tone: "accent", label: "Low 3 (21 %)" },
                { note: "3 acknowledged · 11 resolved" },
              ]}
            />
          </DonutWrap>
        </Panel>

        <Panel title="Synthetic Checks" badge={<FreshBadge label="1 min" />}>
          <Row tone="ok" value="200 OK · 56 ms">
            API Health
          </Row>
          <Row tone="ok" value="412 ms">
            Login-Flow (beide Origins)
          </Row>
          <Row tone="ok" value="7 ms">
            DB Connectivity
          </Row>
          <Row tone="ok" value="OK · OK">
            Geocode MA + DE
          </Row>
          <Row tone="ok" value="4× 200">
            Rechtsseiten (4 URLs)
          </Row>
        </Panel>

        <Panel title="Delivery & Watchdog">
          <Row tone="ok" value="Healthy">
            Mail (Postmark + SPF/DKIM)
          </Row>
          <Row tone="ok" value="Healthy">
            Telegram Bot &amp; Kanal
          </Row>
          <Row tone="ok" value="Running">
            Watchdog-Daemon
          </Row>
          <Row tone="ok" value="12 s">
            Letzter Heartbeat
          </Row>
          <Row tone="info" value="133 / 133">
            Alarm-Texte Owner-lesbar
          </Row>
        </Panel>

        <Panel title="Recent Incidents & Changes">
          <Row tone="crit" value="−41 min">
            Offsite-Check fehlgeschlagen
          </Row>
          <Row tone="warn" value="−2 h">
            Bastion CPU hoch (68 %)
          </Row>
          <Row tone="info" value="−12 h">
            Deploy: Staging-Bündel 1425–1441
          </Row>
          <Row tone="ok" value="−1 T">
            Resolved: Node up (gt-monitor-1)
          </Row>
        </Panel>
      </GridBottom>

      <TriGrid>
        <ChartPanel
          title="Alarm-Aufkommen je Woche"
          badge={<span className="mono muted">8 Wochen</span>}
        >
          <BarChart
            height={90}
            baselineY={45}
            tone="accent"
            bars={[
              { x: 10, y: 55, width: 22, height: 30 },
              { x: 47, y: 40, width: 22, height: 45 },
              { x: 84, y: 60, width: 22, height: 25 },
              { x: 121, y: 30, width: 22, height: 55 },
              { x: 158, y: 50, width: 22, height: 35 },
              { x: 195, y: 62, width: 22, height: 23 },
              { x: 232, y: 58, width: 22, height: 27 },
              { x: 269, y: 66, width: 22, height: 19 },
            ]}
          />
          <div className="sub">Wird die Plattform ruhiger? Trend fällt seit 4 Wochen.</div>
        </ChartPanel>

        <ChartPanel
          title="Disk-Wachstum gt-db-primary-1"
          badge={<span className="mono muted">90 Tage + Prognose</span>}
        >
          <LineChart
            height={90}
            baselineY={20}
            thresholdY={20}
            series={[
              {
                tone: "accent",
                points: "0,72 50,68 100,63 150,57 200,50 250,44",
              },
              {
                tone: "accent",
                points: "250,44 300,32",
                dashed: true,
              },
            ]}
            annotations={[{ x: 295, y: 15, text: "100 %", tone: "crit" }]}
          />
          <div className="sub">Wann brauchen wir Platz? Schnittpunkt mit Rot = Tage-bis-voll.</div>
        </ChartPanel>

        <ChartPanel
          title="Uptime je Woche vs. SLO"
          badge={<span className="mono muted">12 Wochen</span>}
        >
          <LineChart
            height={90}
            baselineY={30}
            thresholdY={30}
            series={[
              {
                tone: "ok",
                points:
                  "0,18 25,20 50,16 75,22 100,17 125,19 150,58 175,20 200,16 225,18 250,15 275,17 300,16",
              },
            ]}
            markers={[{ cx: 150, cy: 58, tone: "warn" }]}
            annotations={[{ x: 295, y: 27, text: "SLO 99,9 %", tone: "warn" }]}
          />
          <div className="sub">
            Halten wir das Versprechen? Ein Ausreißer (Woche 30) fraß Budget — seither wieder
            darüber.
          </div>
        </ChartPanel>
      </TriGrid>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Chip label="gt-app-main-1" />
        <Chip label="STAGING" env />
        <Pill label="1" tone="warn" />
        <Pill label="2" tone="crit" />
        <Bar pct={24} />
        <Bar pct={68} tone="warn" />
        <Bar pct={92} tone="crit" />
      </div>

      <DecisionsSection
        title="Entscheidungs-Punkte Runde 2/3 — Monitoring Cockpit + Detail-Tabs"
        intro='Runde 1 (P1–P6) ist abgenommen ✓ — antworte wie gehabt: „P7 ok / P9 ändern: …"'
      >
        <DecisionCard no="P1–P6" approved>
          <b>Abgenommen am 01.09.</b> — Menü seitlich · 6 Gruppen · Startseite ohne Members · Ask
          Genius im Kopf · Danger Zone sichtbar · Zähl-Pillen.
        </DecisionCard>
        <DecisionCard no="P7">
          <b>Sieben Tabs</b> nach deinem Zielbild (Executive Summary → Historical Trends), sticky
          und per Link teilbar.{" "}
          <DecisionQuestion>Frage: Reihenfolge so in Ordnung?</DecisionQuestion>
        </DecisionCard>
        <DecisionCard no="P9">
          <b>Disk-Semantik nach deinem Zielbild §1.9:</b> „62 % · 87 GiB frei · Tage-bis-voll" in
          EINER kompakten Zelle, Prognose färbt sich bei &lt; 30 Tagen.{" "}
          <DecisionQuestion>Frage: Prognose-Schwelle 30 Tage ok?</DecisionQuestion>
        </DecisionCard>
        <DecisionCard no="✓" approved>
          <b>Monitoring Cockpit KOMPLETT ABGENOMMEN (01.09. 22:2x)</b> — alle 7 Tabs.
        </DecisionCard>
      </DecisionsSection>
    </div>
  );
}
